const INVITE_TOKEN_SESSION_KEY = 'bagtag_invite_token';
const INVITE_TOKEN_LOCAL_KEY = 'bagtag_invite_token';

function parseHashSegment(segment: string): URLSearchParams {
  return segment ? new URLSearchParams(segment) : new URLSearchParams();
}

function extractInviteFromHash(rawHash: string): string | null {
  const inviteMatch = rawHash.match(/(?:^|&)invite=([^&#]+)/);
  if (inviteMatch) return decodeURIComponent(inviteMatch[1]);

  const tokenMatch = rawHash.match(/(?:^|&)token=([^&#]+)/);
  if (tokenMatch) return decodeURIComponent(tokenMatch[1]);

  // invite=TOKEN at start before a second # (broken Supabase redirect)
  const leadingInvite = rawHash.match(/^invite=([^#&]+)/);
  if (leadingInvite) return decodeURIComponent(leadingInvite[1]);

  const leadingToken = rawHash.match(/^token=([^#&]+)/);
  if (leadingToken) return decodeURIComponent(leadingToken[1]);

  return null;
}

/**
 * Supabase magic links can produce a broken double-hash:
 *   /signup#invite=TOKEN#access_token=...
 * Rewrite to one hash Supabase can parse: #access_token=...&invite=TOKEN
 * Must run before createClient / getSession.
 */
export function normalizeMagicLinkHash(): void {
  if (typeof window === 'undefined') return;

  const { pathname, search, hash } = window.location;
  if (!hash) return;

  const rawHash = hash.replace(/^#/, '');
  const accessSplit = rawHash.indexOf('#access_token=');
  if (accessSplit === -1) return;

  const inviteSegment = rawHash.slice(0, accessSplit);
  const authSegment = rawHash.slice(accessSplit + 1);

  const invite =
    extractInviteFromHash(inviteSegment)
    ?? extractInviteFromHash(rawHash);

  if (invite) persistInviteToken(invite);

  let newHash = authSegment;
  if (invite && !authSegment.includes('invite=')) {
    newHash += `&invite=${encodeURIComponent(invite)}`;
  }

  window.history.replaceState(window.history.state, '', `${pathname}${search}#${newHash}`);
}

/** Call before Supabase parses the auth hash so invite survives magic-link redirects. */
export function captureInviteTokenFromUrlEarly(): void {
  if (typeof window === 'undefined') return;

  const fromSearch =
    new URLSearchParams(window.location.search).get('token')
    ?? new URLSearchParams(window.location.search).get('invite');
  if (fromSearch) {
    persistInviteToken(fromSearch);
    return;
  }

  const rawHash = window.location.hash.replace(/^#/, '');
  const fromHash = extractInviteFromHash(rawHash);
  if (fromHash) {
    persistInviteToken(fromHash);
  }
}

export function isSignupEntryPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/signup';
}

export function getInviteTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const fromSearch =
    new URLSearchParams(window.location.search).get('token')
    ?? new URLSearchParams(window.location.search).get('invite');
  if (fromSearch) return fromSearch;

  const rawHash = window.location.hash.replace(/^#/, '');
  return extractInviteFromHash(rawHash)
    ?? parseHashSegment(rawHash.split('#')[0]).get('invite')
    ?? parseHashSegment(rawHash.split('#')[0]).get('token');
}

export function persistInviteToken(token: string): void {
  sessionStorage.setItem(INVITE_TOKEN_SESSION_KEY, token);
  try {
    localStorage.setItem(INVITE_TOKEN_LOCAL_KEY, token);
  } catch {
    // Private mode — sessionStorage still helps same-tab resume.
  }
}

export function getStoredInviteToken(): string | null {
  return sessionStorage.getItem(INVITE_TOKEN_SESSION_KEY)
    ?? (() => {
      try {
        return localStorage.getItem(INVITE_TOKEN_LOCAL_KEY);
      } catch {
        return null;
      }
    })();
}

export function resolveInviteToken(): string | null {
  const fromUrl = getInviteTokenFromUrl();
  if (fromUrl) {
    persistInviteToken(fromUrl);
    return fromUrl;
  }
  return getStoredInviteToken();
}

export function clearInviteToken(): void {
  sessionStorage.removeItem(INVITE_TOKEN_SESSION_KEY);
  try {
    localStorage.removeItem(INVITE_TOKEN_LOCAL_KEY);
  } catch {
    // ignore
  }
}

/** Remove invite token from URL (query + hash) without touching Supabase auth params. */
export function stripInviteTokenFromUrl(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  let changed = false;

  if (url.searchParams.has('token') || url.searchParams.has('invite')) {
    url.searchParams.delete('token');
    url.searchParams.delete('invite');
    changed = true;
  }

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    if (hashParams.has('invite') || hashParams.has('token')) {
      hashParams.delete('invite');
      hashParams.delete('token');
      const remaining = hashParams.toString();
      url.hash = remaining ? `#${remaining}` : '';
      changed = true;
    }
  }

  if (!changed) return;
  const next = url.pathname + url.search + url.hash;
  window.history.replaceState(window.history.state, '', next);
}

function onboardKey(token: string): string {
  return `bagtag_onboard_${token.slice(0, 24)}`;
}

export function persistOnboardingProgress(step: number, form: Record<string, unknown>, token: string): void {
  try {
    localStorage.setItem(onboardKey(token), JSON.stringify({ step, form }));
  } catch {
    // ignore
  }
}

export function restoreOnboardingProgress(token: string): { step: number; form: Record<string, unknown> } | null {
  try {
    const raw = localStorage.getItem(onboardKey(token));
    if (!raw) return null;
    return JSON.parse(raw) as { step: number; form: Record<string, unknown> };
  } catch {
    return null;
  }
}

export function clearOnboardingProgress(token: string): void {
  try {
    localStorage.removeItem(onboardKey(token));
  } catch {
    // ignore
  }
}

export function clearAllInviteState(token?: string): void {
  clearInviteToken();
  if (token) clearOnboardingProgress(token);
  stripInviteTokenFromUrl();
  try {
    localStorage.removeItem('bagtag_onboarding_complete');
  } catch {
    // ignore — matches ONBOARDING_COMPLETE_STORAGE_KEY in onboarding.ts
  }
}
